import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Clock3, Globe, Loader2, Lock } from "lucide-react";
import { AGENT_SKINS, PromptComposer } from "@/components/ui/prompt-card";
import { PromptInput } from "@/components/ui/ai-chat-input";
import { DASHBOARD_SIGNUP_URL } from "@/lib/cta";

/* ─────────────────────────────────────────────────────────────
   The home hero's demo: a prompt is typed into the composer and
   "sent", and the app's canvas — the same three-column map the
   panel shows (internet → services → data, right to left) —
   builds itself: cards land one by one, connections draw in with
   the variable they inject, and each card turns live. Then the
   next agent asks for the next app.

   SSR and reduced-motion visitors get the first scene, finished.
   ───────────────────────────────────────────────────────────── */

const DEVICON = "/frameworks";

type Col = "internet" | "services" | "data";
type DemoNode = {
  id: string;
  col: Col;
  name: string;
  sub: string;
  logo?: string;
  kind?: "domain" | "cron";
  ltr?: boolean;
};
type DemoEdge = { from: string; to: string; label?: string };
type Scene = { skin: number; prompt: string; project: string; url?: string; nodes: DemoNode[]; edges: DemoEdge[] };

const SCENES: Scene[] = [
  {
    skin: 0,
    prompt: "لطفاً یک فروشگاه با Next.js و PostgreSQL بساز و منتشر کن",
    project: "فروشگاه آنلاین",
    url: "shop.gerdoo.app",
    nodes: [
      { id: "web", col: "services", name: "web", sub: "Next.js · main", logo: `${DEVICON}/nextjs/nextjs-original.svg`, ltr: true },
      { id: "pg", col: "data", name: "Postgres", sub: "پایگاه داده", logo: "/logos/postgres.svg" },
      { id: "dom", col: "internet", name: "shop.gerdoo.app", sub: "نشانی عمومی", kind: "domain" },
    ],
    edges: [
      { from: "dom", to: "web" },
      { from: "web", to: "pg", label: "DATABASE_URL" },
    ],
  },
  {
    skin: 1,
    prompt: "یک API با Go بساز و به PostgreSQL و Redis متصل کن",
    project: "API فروشگاه",
    url: "api.gerdoo.app",
    nodes: [
      { id: "api", col: "services", name: "api", sub: "Go · main", logo: `${DEVICON}/go/go-original.svg`, ltr: true },
      { id: "pg", col: "data", name: "Postgres", sub: "پایگاه داده", logo: "/logos/postgres.svg" },
      { id: "redis", col: "data", name: "Redis", sub: "کش", logo: "/logos/redis.svg" },
      { id: "dom", col: "internet", name: "api.gerdoo.app", sub: "نشانی عمومی", kind: "domain" },
    ],
    edges: [
      { from: "dom", to: "api" },
      { from: "api", to: "pg", label: "DATABASE_URL" },
      { from: "api", to: "redis", label: "REDIS_URL" },
    ],
  },
  {
    skin: 2,
    prompt: "ربات تلگرام من را راه‌اندازی کن و ارسال گزارش شبانه را تنظیم کن",
    project: "ربات تلگرام",
    nodes: [
      { id: "bot", col: "services", name: "bot", sub: "Python · خصوصی", logo: `${DEVICON}/python/python-original.svg` },
      { id: "redis", col: "data", name: "Redis", sub: "صف پیام‌ها", logo: "/logos/redis.svg" },
      { id: "cron", col: "services", name: "nightly-report", sub: "هر شب ساعت ۲۳", kind: "cron" },
    ],
    edges: [
      { from: "bot", to: "redis", label: "REDIS_URL" },
      { from: "cron", to: "redis", label: "REDIS_URL" },
    ],
  },
  {
    skin: 3,
    prompt: "یک وبلاگ وردپرس روی دامنهٔ من راه‌اندازی کن",
    project: "وبلاگ",
    url: "blog.example.ir",
    nodes: [
      { id: "wp", col: "services", name: "wordpress", sub: "قالب آماده", logo: "/logos/wordpress.svg" },
      { id: "db", col: "data", name: "MariaDB", sub: "پایگاه داده", logo: "/logos/mariadb.svg" },
      { id: "dom", col: "internet", name: "blog.example.ir", sub: "دامنهٔ شخصی", kind: "domain" },
    ],
    edges: [
      { from: "dom", to: "wp" },
      { from: "wp", to: "db", label: "DATABASE_URL" },
    ],
  },
];

/* Stage geometry, in px; the whole stage scales to the available width. */
const STAGE_W = 880;
const STAGE_H = 280;
const CARD_W = 200;
const CARD_H = 68;
// Spaced so each gap (~150px) fits a variable chip like DATABASE_URL between the cards.
const COL_X: Record<Col, number> = { internet: 785, services: 440, data: 95 };

/*
 * Phones get a portrait stage instead: the three columns become rows, top to
 * bottom (internet → services → data), so the map stays near 1:1 rather than
 * shrinking the landscape stage to under half size.
 */
const TALL_W = 360;
const TALL_H = 372;
const TALL_CARD_W = { single: 220, pair: 164 };
const TALL_ROWS_Y = { three: [46, 186, 326], two: [116, 256] };
const TALL_PAIR_X = [92, 268];
/** Below this container width the portrait stage is used. */
const TALL_BREAKPOINT = 560;

type Pos = { x: number; y: number; w: number };

function layout(scene: Scene, tall: boolean) {
  const pos = new Map<string, Pos>();
  const cols = (["internet", "services", "data"] as Col[]).filter((col) => scene.nodes.some((n) => n.col === col));
  cols.forEach((col, row) => {
    const items = scene.nodes.filter((n) => n.col === col);
    items.forEach((n, i) => {
      if (tall) {
        const ys = cols.length === 3 ? TALL_ROWS_Y.three : TALL_ROWS_Y.two;
        const single = items.length === 1;
        pos.set(n.id, {
          x: single ? TALL_W / 2 : TALL_PAIR_X[i % 2],
          y: ys[row],
          w: single ? TALL_CARD_W.single : TALL_CARD_W.pair,
        });
        return;
      }
      const y = items.length === 1 ? 160 : 112 + (i * 104) / Math.max(1, items.length - 1);
      pos.set(n.id, { x: COL_X[col], y, w: CARD_W });
    });
  });
  return pos;
}

/**
 * The panel's connection shape: horizontal out of one card, a vertical run in
 * the middle of the gap, horizontal into the other — with a small radius at
 * each 90° turn (dashboard: canvasConnections.roundedRoute). The label sits at
 * the middle of the vertical run, which is the line's midpoint.
 */
const CORNER_R = 6;

function edgePath(a: { x: number; y: number }, b: { x: number; y: number }) {
  const hw = CARD_W / 2;
  const leftward = b.x < a.x;
  const sx = leftward ? a.x - hw : a.x + hw;
  const ex = leftward ? b.x + hw : b.x - hw;
  const midX = (sx + ex) / 2;
  const my = (a.y + b.y) / 2;
  if (Math.abs(a.y - b.y) < 0.5) return { d: `M ${sx} ${a.y} L ${ex} ${b.y}`, mx: midX, my };
  const dx1 = Math.sign(midX - sx), dy = Math.sign(b.y - a.y), dx2 = Math.sign(ex - midX);
  const r = Math.min(CORNER_R, Math.abs(b.y - a.y) / 2, Math.abs(midX - sx));
  const d = [
    `M ${sx} ${a.y}`,
    `L ${midX - dx1 * r} ${a.y}`,
    `Q ${midX} ${a.y} ${midX} ${a.y + dy * r}`,
    `L ${midX} ${b.y - dy * r}`,
    `Q ${midX} ${b.y} ${midX + dx2 * r} ${b.y}`,
    `L ${ex} ${b.y}`,
  ].join(" ");
  return { d, mx: midX, my };
}

/**
 * The portrait stage's connection: down out of the upper card, a horizontal
 * run halfway between the rows, down into the lower one. Two lines that share
 * an end would stack their chips there, so the chip goes on the other end's
 * leg (`labelAtEnd`).
 */
function tallEdgePath(a: { x: number; y: number }, b: { x: number; y: number }, labelAtEnd: boolean) {
  const sy = a.y + CARD_H / 2;
  const ey = b.y - CARD_H / 2;
  const midY = (sy + ey) / 2;
  if (Math.abs(a.x - b.x) < 0.5) return { d: `M ${a.x} ${sy} L ${b.x} ${ey}`, mx: a.x, my: midY };
  const dx = Math.sign(b.x - a.x);
  const r = Math.min(CORNER_R, (ey - sy) / 2, Math.abs(b.x - a.x) / 2);
  const d = [
    `M ${a.x} ${sy}`,
    `L ${a.x} ${midY - r}`,
    `Q ${a.x} ${midY} ${a.x + dx * r} ${midY}`,
    `L ${b.x - dx * r} ${midY}`,
    `Q ${b.x} ${midY} ${b.x} ${midY + r}`,
    `L ${b.x} ${ey}`,
  ].join(" ");
  return labelAtEnd ? { d, mx: b.x, my: (midY + ey) / 2 } : { d, mx: a.x, my: (sy + midY) / 2 };
}

type Phase = "typing" | "sending" | "building" | "live" | "clearing";
type DemoState = { scene: number; typed: number; phase: Phase; shown: number; live: number };

const FINISHED: DemoState = { scene: 0, typed: SCENES[0].prompt.length, phase: "live", shown: SCENES[0].nodes.length, live: SCENES[0].nodes.length };

function useDemoTimeline(typing: boolean) {
  const [state, setState] = useState<DemoState>(FINISHED);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) => new Promise<void>((resolve) => { timers.push(window.setTimeout(resolve, ms)); });

    void (async () => {
      let i = 0;
      await wait(3800); // the first, finished scene is read first
      while (!cancelled) {
        setState((s) => ({ ...s, phase: "clearing" }));
        await wait(550);
        if (cancelled) return;
        i = (i + 1) % SCENES.length;
        const scene = SCENES[i];
        setState({ scene: i, typed: 0, phase: "typing", shown: 0, live: 0 });
        await wait(350);
        if (typing) {
          for (let c = 1; c <= scene.prompt.length && !cancelled; c++) {
            setState((s) => ({ ...s, typed: c }));
            await wait(36);
          }
          await wait(500);
          setState((s) => ({ ...s, phase: "sending" }));
          await wait(650);
        }
        setState((s) => ({ ...s, phase: "building" }));
        for (let n = 1; n <= scene.nodes.length && !cancelled; n++) {
          setState((s) => ({ ...s, shown: n }));
          await wait(760);
        }
        for (let n = 1; n <= scene.nodes.length && !cancelled; n++) {
          setState((s) => ({ ...s, live: n }));
          await wait(320);
        }
        setState((s) => ({ ...s, phase: "live" }));
        await wait(4200);
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [typing]);

  return state;
}

/** Picks the landscape or portrait stage for the available width and fits it in. */
function useStageScale() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState({ scale: 1, tall: false });
  // useEffect, not useLayoutEffect: the page is pre-rendered (SSG).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const tall = el.clientWidth < TALL_BREAKPOINT;
      setFit({ tall, scale: Math.min(1, el.clientWidth / (tall ? TALL_W : STAGE_W)) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, ...fit };
}

/**
 * composer:
 * - "card"   the large agent-skinned composer above the map;
 * - "corner" the compact morphing input pinned to the map's top-right corner,
 *            overlapping its edge (read-only: the demo types into it);
 * - "none"   just the map.
 */
export function HeroBuildDemo({ composer = "card" }: { composer?: "card" | "corner" | "none" }) {
  const showComposer = composer === "card";
  // Without a composer there is no prompt to watch being typed: go straight to building.
  const state = useDemoTimeline(composer !== "none");

  const { ref, scale, tall } = useStageScale();
  const stageW = tall ? TALL_W : STAGE_W;
  const stageH = tall ? TALL_H : STAGE_H;
  const scene = SCENES[state.scene];
  const skin = AGENT_SKINS[scene.skin % AGENT_SKINS.length];
  const pos = layout(scene, tall);
  const route = (e: DemoEdge, a: Pos, b: Pos) =>
    tall ? tallEdgePath(a, b, scene.edges.filter((o) => o.from === e.from).length > 1) : edgePath(a, b);
  // Phones: the corner input sits in the flow above the map and only overlaps
  // its top edge, instead of floating over the map itself.
  const cornerInFlow = composer === "corner" && tall;
  const shownIds = new Set(scene.nodes.slice(0, state.shown).map((n) => n.id));
  const liveIds = new Set(scene.nodes.slice(0, state.live).map((n) => n.id));
  const clearing = state.phase === "clearing";

  const note =
    state.phase === "sending" ? (
      <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> ارسال شد</span>
    ) : state.phase === "building" ? (
      <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> در حال راه‌اندازی در گردو…</span>
    ) : state.phase === "live" || clearing ? (
      <span className="inline-flex items-center gap-1.5 text-emerald-600">
        <Check className="h-3.5 w-3.5" aria-hidden /> آنلاین شد{scene.url ? <> · <span dir="ltr">{scene.url}</span></> : ""}
      </span>
    ) : undefined;

  return (
    <div className="relative mx-auto w-full max-w-[980px]">
      {composer === "corner" && (
        <div
          dir="ltr"
          className={
            cornerInFlow
              ? "relative z-20 mx-auto -mb-6 flex w-[calc(100%-1.5rem)] max-w-[340px] justify-center"
              : "absolute -top-7 right-3 z-20 flex w-[min(340px,calc(100%-1.5rem))] justify-end sm:-right-5"
          }
        >
          <PromptInput
            value={scene.prompt.slice(0, state.typed)}
            readOnly
            onSubmit={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
            model={skin.label}
            placeholder="چه می‌خواهید راه‌اندازی کنید؟"
            textDir="rtl"
            voice={false}
            attachments={false}
            collapsedWidth={240}
            expandedWidth={340}
          />
        </div>
      )}
      {showComposer && (
        <div className="relative z-10">
          <PromptComposer text={scene.prompt.slice(0, state.typed)} skin={skin} note={note} project={scene.project} />
        </div>
      )}

      {/* The app canvas, as the panel draws it */}
      <div
        aria-hidden
        className={`relative ${showComposer ? "-mt-6" : ""} overflow-hidden rounded-[1.6rem] border border-black/[0.06] bg-[#fbfaf8]`}
      >
        <div
          ref={ref}
          // With the composer shown, top padding clears it (it overlaps the card's top edge).
          className="relative w-full [background-image:radial-gradient(rgb(0_0_0_/_0.09)_1px,transparent_1px)] [background-size:18px_18px]"
          style={{ height: stageH * scale + (showComposer || cornerInFlow ? 40 : 16) }}
        >
          <div
            className={`absolute ${showComposer || cornerInFlow ? "top-8" : "top-2"}`}
            style={{ left: "50%", width: stageW, height: stageH, transform: `translateX(-50%) scale(${scale})`, transformOrigin: "top center" }}
          >

            <AnimatePresence mode="wait">
              <motion.div
                key={state.scene}
                className="absolute inset-0"
                initial={{ opacity: 1 }}
                animate={{ opacity: clearing ? 0 : 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <svg className="absolute inset-0 overflow-visible" width={stageW} height={stageH}>
                  {scene.edges.map((e) => {
                    const a = pos.get(e.from), b = pos.get(e.to);
                    if (!a || !b || !shownIds.has(e.from) || !shownIds.has(e.to)) return null;
                    const { d } = route(e, a, b);
                    const done = liveIds.has(e.from) && liveIds.has(e.to);
                    return (
                      <motion.path
                        key={`${e.from}-${e.to}`}
                        d={d}
                        fill="none"
                        strokeWidth={2}
                        strokeLinecap="round"
                        stroke={done ? "#f6821f" : "#f9b37a"}
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                      />
                    );
                  })}
                </svg>

                {scene.nodes.map((n) => {
                  const p = pos.get(n.id);
                  if (!p || !shownIds.has(n.id)) return null;
                  return <DemoCard key={n.id} node={n} x={p.x} y={p.y} w={p.w} live={liveIds.has(n.id)} />;
                })}

                {/* Variable chips go last so they always sit above the cards. */}
                {scene.edges.map((e) => {
                  const a = pos.get(e.from), b = pos.get(e.to);
                  if (!e.label || !a || !b || !shownIds.has(e.from) || !shownIds.has(e.to)) return null;
                  const { mx, my } = route(e, a, b);
                  return (
                    <motion.span
                      key={`l-${e.from}-${e.to}`}
                      dir="ltr"
                      className="absolute z-10 whitespace-nowrap rounded-md border border-gerdoo-200 bg-white px-1.5 py-0.5 font-mono text-[10.5px] text-gerdoo-700 shadow-sm"
                      // Centred on the line's midpoint through motion's own x/y: a
                      // Tailwind translate would be overwritten by its scale transform.
                      style={{ left: mx, top: my, x: "-50%", y: "-50%" }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.45, duration: 0.25 }}
                    >
                      {e.label}
                    </motion.span>
                  );
                })}

              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoCard({ node, x, y, w, live }: { node: DemoNode; x: number; y: number; w: number; live: boolean }) {
  const domain = node.kind === "domain";
  return (
    <motion.div
      className="absolute flex flex-col justify-center gap-1.5 rounded-xl border border-black/[0.07] bg-white px-3 text-start shadow-[0_1px_2px_rgb(0_0_0_/_0.04),0_10px_24px_-14px_rgb(66_40_14_/_0.35)]"
      style={{ left: x - w / 2, top: y - CARD_H / 2, width: w, height: CARD_H }}
      initial={{ opacity: 0, scale: 0.86, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 26 }}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ink-50 ring-1 ring-inset ring-black/[0.05]">
          {node.logo ? (
            <img src={node.logo} alt="" className="h-5 w-5" loading="lazy" />
          ) : domain ? (
            <Globe className="h-4 w-4 text-ink-500" />
          ) : (
            <Clock3 className="h-4 w-4 text-ink-500" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          {/* Latin names keep their own direction but align right, like the Persian line under them. */}
          <span dir={domain || node.ltr ? "ltr" : undefined} className="block truncate text-right text-[13px] font-bold leading-5 text-ink-950">
            {node.name}
          </span>
          <span className="block truncate text-[11px] leading-4 text-ink-500">{node.sub}</span>
        </span>
        {domain && <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-500" />}
      </div>
      {!domain && (
        <span className="flex items-center gap-1.5 text-[11px] font-semibold">
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500" : "animate-pulse bg-[#f6821f]"}`} />
          <span className={live ? "text-emerald-700" : "text-gerdoo-700"}>{live ? "فعال" : "در حال ساخت"}</span>
        </span>
      )}
    </motion.div>
  );
}
